import React from "react";

import type { IPrizeRow } from "@/@types/gamification";

interface ProbabilityPieProps {
  prizes: Array<Partial<IPrizeRow>> | undefined;
}

const COLORS = [
  "#f5c518",
  "#7a1e3a",
  "#e8871e",
  "#4b303c",
  "#2f9e6e",
  "#5b7fd6",
];

const RADIUS = 56;
const CENTER = 64;

function polar(angleDeg: number): { x: number; y: number } {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: CENTER + RADIUS * Math.cos(rad),
    y: CENTER + RADIUS * Math.sin(rad),
  };
}

function slicePath(startAngle: number, endAngle: number): string {
  const start = polar(startAngle);
  const end = polar(endAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${CENTER} ${CENTER} L ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
}

export const ProbabilityPie: React.FC<ProbabilityPieProps> = ({ prizes }) => {
  const slices = (prizes ?? []).map((prize, index) => ({
    probability: Number(prize?.probability) || 0,
    value: Number(prize?.value) || 0,
    label: prize?.label,
    color: COLORS[index % COLORS.length],
    index,
  }));
  const total = slices.reduce((sum, slice) => sum + slice.probability, 0);

  if (total <= 0) {
    return (
      <p className="text-sm text-gray-500">
        Preencha as porcentagens para visualizar a distribuição.
      </p>
    );
  }

  let cursor = 0;
  const drawn = slices
    .filter((slice) => slice.probability > 0)
    .map((slice) => {
      const startAngle = (cursor / total) * 360;
      cursor += slice.probability;
      const endAngle = (cursor / total) * 360;

      const isFull = endAngle - startAngle >= 359.999;
      return { ...slice, startAngle, endAngle, isFull };
    });

  return (
    <div className="flex flex-col items-center gap-4">
      <svg
        viewBox="0 0 128 128"
        className="h-40 w-40"
        role="img"
        aria-label="Distribuição dos prêmios"
      >
        {drawn.map((slice) =>
          slice.isFull ? (
            <circle
              key={slice.index}
              cx={CENTER}
              cy={CENTER}
              r={RADIUS}
              fill={slice.color}
            />
          ) : (
            <path
              key={slice.index}
              d={slicePath(slice.startAngle, slice.endAngle)}
              fill={slice.color}
              stroke="#fff"
              strokeWidth="1.5"
            />
          ),
        )}
      </svg>

      <ul className="w-full space-y-1.5">
        {slices.map((slice) => (
          <li
            key={slice.index}
            className="flex items-center gap-2 text-xs text-gray-600"
          >
            <span
              className="h-3 w-3 shrink-0 rounded-sm"
              style={{ backgroundColor: slice.color }}
            />
            <span className="font-medium">{slice.probability}%</span>
            <span className="truncate">
              {slice.label || `R$ ${slice.value}`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
